
import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { generateTitle, processAnalysis } from '@/config/autoGeneration'
import { updateAnalysisIaReport } from '@/config/api'
import { API_BASE_URL } from '@/config/api'
import type { EditingState } from '@/types/autoGeneration'

interface UseAutoGenerationAPIProps {
  analysisId: number
  lang: string
  jwt: string
  userEmail: string
  editingState: EditingState
  transcriptionsRef: React.MutableRefObject<any[]>
}

export interface GeneratedReportResponse {
  content: {[key: string]: string}
  editedSections: string[]
}

export const useAutoGenerationAPI = ({
  analysisId,
  lang,
  jwt,
  userEmail,
  editingState,
  transcriptionsRef
}: UseAutoGenerationAPIProps) => {
  const queryClient = useQueryClient()
  const [titleError, setTitleError] = useState(false)
  const [reportError, setReportError] = useState(false)

  // Check if title can be generated
  const canGenerateTitle = useCallback(async (): Promise<boolean> => {
    try {
      const response = await axios.get(`${API_BASE_URL}/analyses/${analysisId}`, {
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      })

      const analysis = response.data.data
      const titleEdited = analysis.attributes.titleEdited
      
      console.log('🏷️ Title edited status:', titleEdited)
      
      if (titleEdited) {
        console.log('⏸️ Skipping title generation - title has been manually edited')
        return false
      }
      
      return true
    } catch (error) {
      console.error('❌ Error checking title edited status:', error)
      return false
    }
  }, [analysisId, jwt])

  // Generate title content with abort signal support  
  const generateTitleAPI = useCallback(async (abortSignal?: AbortSignal) => {
    console.log('📝 [TITLE] Starting title generation...')
    
    if (!transcriptionsRef.current.length) {
      throw new Error('No transcriptions available for title generation')
    }

    // Convert transcriptions array to string
    const fullTranscript = transcriptionsRef.current
      .map(t => t.transcript || t.text || '')
      .filter(text => text.trim().length > 0)
      .join(' ')

    console.log('📝 [TITLE] Full transcript length:', fullTranscript.length)
    
    if (fullTranscript.length < 20) {
      throw new Error('Insufficient transcript content for title generation')
    }

    console.log('📝 [TITLE] Calling generateTitle API...')
    
    // Check if aborted before making API call
    if (abortSignal?.aborted) {
      throw new Error('Title generation cancelled')
    }
    
    const response = await generateTitle(
      lang,
      fullTranscript,
      jwt,
      userEmail,
      abortSignal
    )

    console.log('📝 [TITLE] API response:', response)

    if (response && (response.title || response.answer)) {
      const generatedTitle = response.title || response.answer
      
      console.log('📝 [TITLE] Generated title:', generatedTitle)
      
      // Check if aborted before updating
      if (abortSignal?.aborted) {
        throw new Error('Title generation cancelled')
      }
      
      // Update the analysis subject field with the generated title
      await axios.put(`${API_BASE_URL}/analyses/${analysisId}`, {
        data: { 
          subject: generatedTitle
        }
      }, {
        headers: {
          Authorization: `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        signal: abortSignal
      })

      // Invalidate queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['analysis', analysisId.toString()] })
      queryClient.invalidateQueries({ queryKey: ['analyses'] })

      console.log('✅ [TITLE] Title updated successfully')
      return { title: generatedTitle }
    } else {
      throw new Error('No title in response')
    }
  }, [analysisId, lang, jwt, userEmail, queryClient, transcriptionsRef])

  // Generate report content with abort signal support
  const generateReportAPI = useCallback(async (abortSignal?: AbortSignal): Promise<GeneratedReportResponse> => {
    console.log('📊 [REPORT] Starting report generation...')
    
    if (!transcriptionsRef.current.length) {
      throw new Error('No transcriptions available for report generation')
    }

    console.log('📊 [REPORT] Calling processAnalysis API...')
    
    // Check if aborted before making API call
    if (abortSignal?.aborted) {
      throw new Error('Report generation cancelled')
    }
    
    const response = await processAnalysis(
      analysisId.toString(),
      lang,
      jwt,
      userEmail,
      abortSignal
    )

    console.log('📊 [REPORT] API response:', response)

    if (response && response.answer) {
      let parsedContent
      
      try {
        parsedContent = JSON.parse(response.answer)
        console.log('📊 [REPORT] Parsed content:', parsedContent)
      } catch (parseError) {
        console.error('❌ [REPORT] Failed to parse response:', parseError)
        throw new Error('Invalid JSON response from report generation')
      }
      
      // Check if aborted before processing sections
      if (abortSignal?.aborted) {
        throw new Error('Report generation cancelled')
      }
      
      // Update each section in the iaReport
      for (const [sectionKey, content] of Object.entries(parsedContent)) {
        if (typeof content === 'string' && !editingState[sectionKey]) {
          console.log('📊 [REPORT] Updating section:', sectionKey)
          
          // Check if aborted before each update
          if (abortSignal?.aborted) {
            throw new Error('Report generation cancelled')
          }
          
          await updateAnalysisIaReport(analysisId, sectionKey, content)
        }
      }

      // Invalidate queries to refresh the analysis data
      queryClient.invalidateQueries({ queryKey: ['analysis', analysisId.toString()] })

      console.log('✅ [REPORT] Report updated successfully')
      
      // Extract editedSections from the response if available
      const editedSections = response.editedSections || Object.keys(parsedContent)
      
      return { 
        content: parsedContent,
        editedSections 
      }
    } else {
      throw new Error('No content in response')
    }
  }, [analysisId, lang, jwt, userEmail, editingState, queryClient, transcriptionsRef])

  return {
    canGenerateTitle,
    generateTitleAPI,
    generateReportAPI,
    titleError,
    reportError,
    setTitleError,
    setReportError
  }
}
